import React, { useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConsumptionReport from './ConsumptionReport';
import { generatePDF } from '../../../../../utils/reportUtils';
import { usePackageConsumptionReport } from './consumptionDetails.helper';
import ReportDialog from '../../../../../components/Reports/ReportDialog';
import { usePatientPackageStore } from '../../../store';

type ConsumptionReportGenProps = {
  open: boolean;
  onClose: () => void;
  clinicId: number | null;
};

const ConsumptionReportGen: React.FC<ConsumptionReportGenProps> = ({
  open,
  onClose,
  clinicId,
}) => {
  const { t } = useTranslation('Packagebilling');
  const reportRef = useRef<HTMLDivElement>(null);
  const storedPackageId = usePatientPackageStore(state => state.patientPackageId);

  // Fetch report data from API
  const { data: reportData, isLoading, isError } = usePackageConsumptionReport(
    clinicId,
    storedPackageId,
    open // Only fetch when dialog is open
  );

  const handleDownloadPDF = () => {
    if (reportRef.current && reportData) {
      const filename = `Consumption_Report_${storedPackageId}_${new Date().toISOString().slice(0, 10)}`;
      generatePDF(reportRef.current, filename);
    }
  };

  const handlePrint = () => window.print();

  // Show report if we have package info, even if no services/pharmacy consumed yet
  const hasData = reportData && reportData.package_info;

  return (
    <ReportDialog
      open={open}
      onClose={onClose}
      title={t('consumption_details.title')}
      onDownload={hasData ? handleDownloadPDF : undefined}
      onPrint={hasData ? handlePrint : undefined}
      downloadLabel={t('consumption_details.export_pdf')}
      printLabel={t('consumption_details.print_report', 'Print Report')}
      maxWidth="xl"
    >
      {isLoading ? (
        <Box data-testid="consumption-report-gen-loading" sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {t('consumption_details.loading_data', 'Loading consumption data...')}
          </Typography>
        </Box>
      ) : isError ? (
        <Box data-testid="consumption-report-gen-error" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {t('consumption_details.report_load_failed', 'Failed to load report data. Please try again.')}
          </Typography>
        </Box>
      ) : hasData ? (
        <div ref={reportRef} data-testid="consumption-report-gen-content">
          <ConsumptionReport reportData={reportData} />
        </div>
      ) : (
        <Box data-testid="consumption-report-gen-empty" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('consumption_details.no_items_found')}
          </Typography>
        </Box>
      )}
    </ReportDialog>
  );
};

export default ConsumptionReportGen;
